import { CSSObject } from "@emotion/react";

export type Result = {
	rank: number
	index: string;
	affinity: number;
	peptide_file_path: string;
	receptor_file_path: string;
};

export type ResultParams = {
	job_id: string;
};

export type ReceptorSortInfo = {
	receptor_name: string;
	min_rank: number;
	min_energy: number;
	min_rmsd: number;
};

/** Controls color of the "bubble" (MUI Paper) around elements in the page. */
export const bubbleColor: string = "#FFFFFF";

/** Controls color of the navigation bar at the top of every page. */
export const navbarColor: string = "#0076B6";

/** Controls color of the navigation bar. */
export const navbarTextColor: string = "#FFFFFF";

/** Controls color of the footer at the bottom of every page. */
export const footerColor: string = navbarColor;

/** Controls the general background color of every page. */
export const pageBackgroundColor: string = "#B0B7BC";

/** Controls how the color of links changes when hovered over. */
export const linkHoverColor: string = "#00AEEF";

/** Controls the fonts used on the website. */
export const fontFamily: string = `'Segoe UI', "Tahoma", "Geneva", "Verdana", "sans-serif"`;

/** Controls the general text color of the website unless overridden. */
export const textColor: string = "#000000";

/** Styles to override in the MUI theme. */
export const cssStyleOverrides: CSSObject = {
	/** Styling for input containers in an HTML `<form>` element. */
	form: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
	},
	/** Styling for a single page. */
	".page": {
		flex: "1 1 auto",
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		marginBlock: "2rem",
	},
	/** Styling for any necessary text-based links (including the title). */
	".link": {
		textDecoration: "none",
		color: navbarTextColor,
		textAlign: "center",
	},
	/**
	 * Change the color of a specified link when hovered over
	 * (including the title).
	 * */
	".link:hover": {
		color: linkHoverColor,
	},
	/** Remove the default styling for file input elements. */
	'input[type="file"]': {
		display: "none",
	},
	/** Styling for the whole page (page+header+footer). Make sure its minimum height equals to the browser window.
	 * This helps footer to stick at the bottom.
	 * */
	".wholePage": {
		display: "flex",
		flexDirection: "column",
		minHeight: "100vh",
	},
	/** Styling for footer. Make sure it sticks at the bottom of the page.*/
	"footer": {
  		flex: "0 1 auto",
	},
};

/** Base URLs used for API calls. */
// export const BACKEND_BASE_URL: string = "https://dinc-ensemble.kavrakilab.rice.edu/api";
// export const BACKEND_BASE_URL: string = "http://localhost:5001";
export const BACKEND_BASE_URL = "/api";
// export const FRONT_BASE_URL: string = "https://dinc-ensemble.kavrakilab.rice.edu";
// export const FRONT_BASE_URL: string = "http://localhost:3000";
// This is a placeholder for the frontend base URL, which will be replaced during the build process.
// It should be set to the actual URL where the frontend is hosted.
export const FRONT_BASE_URL = "https://stegg.kavrakilab.rice.edu";

/** Dummy file needed for initialization of `FileComponent` instances. */
export const dummyFile = new File([""], "file.txt");

/**
 * Map each contributor to their personal profile link
 * (on the Acknowledgments & Disclaimers page).
 */
export const contributorLinks: { [key: string]: string } = {
	"Lydia E. Kavraki": "https://profiles.rice.edu/faculty/lydia-e-kavraki",
	"Dinler A. Antunes": "https://dinlerantunes.com/",
	"Mauricio M. Rigo":
		"https://www.linkedin.com/in/mauricio-menegatti-rigo-46a297a7",
	"Gregory Lizée":
		"http://faculty.mdanderson.org/Gregory_Lizee/Default.asp?SNID=961720063",
	"Jayvee Abella": "https://abella.ai/",
	"Aravind Kuchibhatla": "https://www.linkedin.com/in/aravind-kuchibhatla",
	"Nicole Qu":
		"https://www.linkedin.com/in/yun-shan-nicole-qu?original_referer=https%3A%2F%2Fwww.google.com%2F",
	"Anja Conev": "https://anjaconev.github.io/about/",
};

/** Valid prefixes given the initial letter of the prefix. */
export const prefixMap: { [key: string]: string[] } = {
	p: ["pS", "pT", "pY"],
	n: ["nC", "nP", "nY", "nW"],
	a: ["aK"],
	c: ["cK", "cR"],
	m: ["mmK", "mdK", "mtK"],
	x: ["xhC", "xoC", "xdC"],
	o: ["oM"],
};

/** Relevant error to display for the user-inputted peptide. */
export const prefixError = (prefix: keyof typeof prefixMap) => {
	return `Accepted for "${prefix}" prefix: [${prefixMap[prefix].join(", ")}]`;
};

/** Possible choices in the MHC dropdown list. */
export const mhcChoices: string[] = [
	"Aime-128",
	"Anpl-UAA*01",
	"Anpl-UAA*SD",
	"BoLA-2*018:01",
	"BoLA-6*013:01",
	"DLA-88*001:04",
	"DLA-88*508:01",
	"Eqca-N*006:02",
	"FLA-E*18:01",
	"Gaga-BF2*002:01:01",
	"Gaga-BF2*004:01:01",
	"Gaga-BF2*012:01:01",
	"Gaga-BF2*014:01:01",
	"Gaga-BF2*015:01:01",
	"Gaga-BF2*021:01:01",
	"H2-Db",
	"H2-Dd",
	"H2-Kb",
	"H2-Kd",
	"H2-Kk",
	"H2-Ld",
	"H2-M3",
	"H2-Q7",
	"H2-T23",
	"HLA-A*01:01",
	"HLA-A*02:01",
	"HLA-A*02:03",
	"HLA-A*02:06",
	"HLA-A*02:07",
	"HLA-A*02:765",
	"HLA-A*02:870",
	"HLA-A*03:01",
	"HLA-A*11:01",
	"HLA-A*24:02",
	"HLA-A*24:50",
	"HLA-A*30:01",
	"HLA-A*30:03",
	"HLA-A*68:01",
	"HLA-A*68:02",
	"HLA-B*07:02",
	"HLA-B*08:01",
	"HLA-B*14:02",
	"HLA-B*15:01",
	"HLA-B*15:02",
	"HLA-B*18:01",
	"HLA-B*27:03",
	"HLA-B*27:04",
	"HLA-B*27:05",
	"HLA-B*27:06",
	"HLA-B*27:09",
	"HLA-B*35:01",
	"HLA-B*35:08",
	"HLA-B*37:01",
	"HLA-B*39:01",
	"HLA-B*40:01",
	"HLA-B*40:02",
	"HLA-B*41:04",
	"HLA-B*42:01",
	"HLA-B*42:02",
	"HLA-B*44:02",
	"HLA-B*44:03",
	"HLA-B*44:05",
	"HLA-B*46:01",
	"HLA-B*51:01",
	"HLA-B*52:01",
	"HLA-B*53:01",
	"HLA-B*57:01",
	"HLA-B*57:03",
	"HLA-B*57:06",
	"HLA-B*58:01",
	"HLA-B*58:11",
	"HLA-B*81:01",
	"HLA-C*01:02",
	"HLA-C*03:04",
	"HLA-C*04:01",
	"HLA-C*05:01",
	"HLA-C*06:02",
	"HLA-C*07:02",
	"HLA-C*08:01",
	"HLA-C*08:02",
	"HLA-C*14:02",
	"HLA-E*01:01",
	"HLA-E*01:03",
	"HLA-G*01:01",
	"HLA-G*01:04",
	"Mafa-A1*001:01:01",
	"Mamu-A1*002:01:01:01",
	"Mamu-B*017:01:01:01",
	"Onmy-UBA*18:01",
	"PTAL-N*01:01",
	"Rano-A1*b",
	"RLA-A*01",
	"SLA-1*01:01",
	"SLA-1*04:01:01",
	"SLA-1*07:04",
	"SLA-1*13:01",
	"SLA-1*14:02",
	"SLA-1*15:01",
	"SLA-2*04:02:01",
	"SLA-2*11:04",
	"Trvu-UB*01:01",
	"Xela-UAAg",
];
